import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getSocketOrigin } from '../services/api';

// Snapchat-style emoji set (bottom sheet grid).
const EMOJIS = [
  '😀', '😁', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌',
  '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😜', '🤪',
  '🤗', '🤭', '🤫', '🤔', '😐', '😑', '😶', '🙄', '😏', '😣',
  '😥', '😮', '🤐', '😯', '😪', '😫', '🥱', '😴', '😔', '😷',
  '🤒', '🤕', '🤢', '🤮', '🥴', '😵', '🤯', '🤠', '🥳', '😎',
  '🤓', '🧐', '😕', '😟', '🙁', '😢', '😭', '😤', '😠', '😡',
  '🤬', '😳', '🥺', '😱', '😨', '😰', '😓', '🫶', '👍', '👎',
  '👊', '✊', '🤛', '🤜', '👏', '🙌', '🤝', '🙏', '💪', '🦾',
  '🔥', '❤️', '💕', '💋', '✨', '🎉', '💯', '👀', '💀', '🙈',
  '🙉', '🙊', '🐶', '🌹', '☀️',
];

const REPORT_REASONS = [
  'Nudity or sexual activity',
  'Hate speech or symbols',
  'Scam or fraud',
  'Violence or dangerous organizations',
  'Sale of illegal or regulated goods',
  'Bullying or harassment',
  'Pretending to be someone else',
  'Intellectual property violation',
  'Suicide, self-injury or eating disorder'
];

function apiBase() {
  return `${getSocketOrigin()}/api/public/chat`;
}

function visitorHeaders() {
  const id = window.localStorage.getItem('cl_visitor') || '';
  return id ? { 'x-chat-visitor': id } : {};
}

function rememberVisitor(id) {
  if (id) window.localStorage.setItem('cl_visitor', id);
}

async function publicFetch(path, options = {}) {
  const headers = { ...(options.headers || {}), ...visitorHeaders() };
  const response = await fetch(`${apiBase()}${path}`, {
    ...options,
    headers,
    credentials: 'include'
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const raw = data.error;
    const message = typeof raw === 'string'
      ? raw
      : (raw && raw.message) || JSON.stringify(raw) || 'Request failed';
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  rememberVisitor(data.visitorId);
  return data;
}

function hostQuery() {
  const params = new URLSearchParams({
    host: window.location.hostname,
    path: window.location.pathname
  });
  return params.toString();
}

function formatTime(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (date.toDateString() === new Date().toDateString()) return `Today at ${time}`;
  return `${date.toLocaleDateString([], { weekday: 'long' })} at ${time}`;
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Humanized typing duration that scales with reply length (chars).
function humanizedTypingMs(length) {
  const base = 800 + Math.floor(Math.random() * 1200); // 0.8–2s
  const perChar = 60 + Math.floor(Math.random() * 60); // 60–120ms/char
  return Math.min(base + length * perChar, 9000); // cap ~9s
}

function linkify(text) {
  const parts = String(text || '').split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => (
    /^https?:\/\//.test(part)
      ? <a key={index} href={part} target="_blank" rel="noreferrer" className="underline break-all">{part}</a>
      : <span key={index}>{part}</span>
  ));
}

function Avatar({ src, name, size = 'md', dark }) {
  const dim = size === 'lg' ? 'h-28 w-28' : 'h-[70px] w-[70px]';
  return (
    <div className={`relative shrink-0 ${dim}`}>
      {src ? (
        <img src={src} alt="" className={`${dim} rounded-full object-cover`} />
      ) : (
        <div className={`${dim} rounded-full ${dark ? 'bg-zinc-700 text-white' : 'bg-gray-300 text-gray-700'} flex items-center justify-center text-sm`}>
          {(name || '?').slice(0, 1)}
        </div>
      )}
      <span className={`absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-green-500 ring-2 ${dark ? 'ring-black' : 'ring-white'}`} />
    </div>
  );
}

// ---- SVG icons (outline, currentColor) ----
function Icon({ children, size = 22, className = '' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const CameraIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M14.5 4h-5L7.5 7H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-2.5l-2-3z" />
    <circle cx="12" cy="13" r="3.2" />
  </Icon>
);

const MicIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
  </Icon>
);

const SmileIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5s1.2 1.8 3.5 1.8 3.5-1.8 3.5-1.8" />
    <path d="M9 9.5h.01M15 9.5h.01" />
  </Icon>
);

const ImageIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <circle cx="8.5" cy="9" r="1.5" />
    <path d="M21 15l-4.5-4.5L7 20" />
  </Icon>
);

const SendIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22l-4-9-9-4z" />
  </Icon>
);

const PhoneIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.13.96.36 1.9.7 2.8a2 2 0 0 1-.45 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.45c.9.34 1.84.57 2.8.7A2 2 0 0 1 22 16.9z" />
  </Icon>
);

const DotsIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    <circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" />
  </Icon>
);

const CloseIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

const SunIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Icon>
);

const MoonIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </Icon>
);

const BackIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M15 18l-6-6 6-6" />
  </Icon>
);

const CheckIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);

export default function PublicChat() {
  const [phase, setPhase] = useState('loading');
  const [profile, setProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [acceptedAt, setAcceptedAt] = useState(null);
  const [text, setText] = useState('');
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [lightbox, setLightbox] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportDone, setReportDone] = useState(false);
  const [typing, setTyping] = useState(false);
  const [dark, setDark] = useState(() => window.localStorage.getItem('cl_theme') === 'dark');
  const scroller = useRef(null);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const accepting = useRef(false);
  const revealToken = useRef(0);

  const query = useMemo(() => hostQuery(), []);

  function toggleTheme() {
    setDark((prev) => {
      const next = !prev;
      window.localStorage.setItem('cl_theme', next ? 'dark' : 'light');
      return next;
    });
  }

  function submitReport() {
    setReportDone(true);
    window.setTimeout(() => {
      setReportOpen(false);
      setReportDone(false);
    }, 1800);
  }

  const c = {
    root: dark ? 'bg-black text-white' : 'bg-white text-black',
    border: dark ? 'border-white/10' : 'border-black/10',
    sub: dark ? 'text-zinc-400' : 'text-gray-500',
    faint: dark ? 'text-zinc-500' : 'text-gray-400',
    bot: dark ? 'bg-zinc-800' : 'bg-gray-100',
    user: 'bg-blue-600 text-white',
    sheet: dark ? 'bg-zinc-900 border-white/10' : 'bg-white border-black/10',
    composer: dark ? 'bg-zinc-900' : 'bg-gray-100',
    icon: dark ? 'text-zinc-300 hover:text-white' : 'text-gray-700 hover:text-gray-900',
    placeholder: dark ? 'placeholder:text-zinc-500' : 'placeholder:text-gray-400',
    modal: dark ? 'bg-zinc-900' : 'bg-white',
    modalBody: dark ? 'text-zinc-400' : 'text-gray-500',
    backdrop: dark ? 'bg-black/70' : 'bg-black/40',
    lightbox: dark ? 'bg-black/90' : 'bg-black/80',
    menu: dark ? 'bg-zinc-900 border-white/10' : 'bg-white border-black/10',
    hover: dark ? 'hover:bg-white/5' : 'hover:bg-black/5',
  };

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    (async () => {
      try {
        const data = await publicFetch(`/session?${query}`, { method: 'POST' });
        if (cancelled) return;
        setProfile(data.profile);
        setAcceptedAt(data.acceptedAt);
        setMessages(data.messages || []);
        setPhase(data.accepted ? 'chat' : 'review');
        if (!data.accepted) {
          const delay = 3000 + Math.floor(Math.random() * 2000);
          timer = window.setTimeout(() => {
            if (!cancelled) acceptChat();
          }, delay);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setPhase('missing');
        }
      }
    })();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [messages, phase, typing]);

  useEffect(() => () => stopCamera(), []);

  async function reveal(existing, created) {
    const token = ++revealToken.current;
    let shown = existing.slice();
    setMessages(shown);
    for (const item of created || []) {
      if (revealToken.current !== token) return;
      if (item.delayBeforeMs) await wait(item.delayBeforeMs);
      if (revealToken.current !== token) return;
      shown = [...shown, item];
      setMessages(shown);
    }
  }

  async function acceptChat() {
    if (accepting.current) return;
    accepting.current = true;
    try {
      const data = await publicFetch(`/accept?${query}`, { method: 'POST' });
      setProfile(data.profile);
      setAcceptedAt(data.acceptedAt);
      setPhase('chat');
      const createdIds = new Set((data.created || []).map((item) => item.id));
      const prior = (data.messages || []).filter((item) => !createdIds.has(item.id));
      if (data.already) setMessages(data.messages || []);
      else await reveal(prior, data.created || []);
    } catch (err) {
      accepting.current = false;
      setError(err.message);
    }
  }

  async function sendPayload(body, isForm, optimistic) {
    setSending(true);
    setError('');
    try {
      const data = await publicFetch(`/messages?${query}`, {
        method: 'POST',
        body,
        headers: isForm ? {} : { 'Content-Type': 'application/json' }
      });
      // Pause a random 2–12s after "Seen just now" before the bot starts typing.
      await wait(2000 + Math.floor(Math.random() * 10000));
      // Typing indicator for a humanized duration based on the reply length.
      setTyping(true);
      const replyLength = (data.created || []).reduce(
        (sum, item) => sum + String(item.content || '').length,
        0
      );
      await wait(humanizedTypingMs(replyLength));
      setTyping(false);
      const createdIds = new Set((data.created || []).map((item) => item.id));
      const serverMessages = (data.messages || []).filter((item) => item.id !== optimistic?.id);
      const prior = serverMessages.filter((item) => !createdIds.has(item.id));
      await reveal(prior, data.created || []);
    } catch (err) {
      setTyping(false);
      setError(err.message);
    } finally {
      setSending(false);
    }
  }

  function optimisticBubble(contentType, content) {
    const id = `local-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
    const bubble = {
      id,
      sender: 'user',
      contentType,
      content,
      timestamp: new Date(),
      status: 'delivered',
      local: true
    };
    setMessages((prev) => [...prev, bubble]);
    const seenDelay = 900 + Math.floor(Math.random() * 1800);
    window.setTimeout(() => {
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, status: 'seen', seenAt: new Date() } : m))
      );
    }, seenDelay);
    return bubble;
  }

  function sendText(event) {
    event?.preventDefault();
    const value = text.trim();
    if (!value || sending) return;
    setText('');
    setEmojiOpen(false);
    const bubble = optimisticBubble('text', value);
    sendPayload(JSON.stringify({ text: value, host: window.location.hostname, path: window.location.pathname }), false, bubble);
  }

  function sendFile(file, field) {
    if (!file || sending) return;
    const isImage = field === 'image';
    const optimisticContent = isImage ? URL.createObjectURL(file) : '';
    const bubble = optimisticBubble(isImage ? 'image' : 'audio', optimisticContent);
    const form = new FormData();
    form.append(field, file);
    form.append('host', window.location.hostname);
    form.append('path', window.location.pathname);
    sendPayload(form, true, bubble);
  }

  async function openCamera() {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      setCameraOpen(true);
      window.setTimeout(() => {
        if (videoRef.current) videoRef.current.srcObject = stream;
      }, 0);
    } catch (_) {
      setError('Camera permission is needed to take a photo');
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
  }

  function takePhoto() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 720;
    canvas.height = video.videoHeight || 960;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (blob) sendFile(new File([blob], 'photo.jpg', { type: 'image/jpeg' }), 'image');
      stopCamera();
    }, 'image/jpeg', 0.9);
  }

  async function toggleRecord() {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size) sendFile(new File([blob], 'voice.webm', { type: blob.type }), 'audio');
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch (_) {
      setError('Microphone permission is needed for a voice note');
    }
  }

  function renderStatus(msg) {
    if (msg.sender !== 'user' || msg.local !== true) return null;
    const stamp = formatTime(msg.timestamp);
    const seen = msg.status === 'seen';
    return (
      <span className={`mt-0.5 block text-right text-[11px] ${c.faint}`}>
        {seen ? 'Seen just now' : 'Delivered'} · {stamp}
      </span>
    );
  }

  if (phase === 'loading') {
    return <div className={`min-h-[100dvh] ${dark ? 'bg-black' : 'bg-white'}`} />;
  }

  if (phase === 'missing') {
    return (
      <div className={`min-h-[100dvh] ${c.root} flex items-center justify-center p-6 text-center`}>
        <p>{error || 'This chat link is not available.'}</p>
      </div>
    );
  }

  if (phase === 'review' && profile) {
    return (
      <div className={`min-h-[100dvh] ${c.root} flex items-center justify-center p-4`}>
        <div className={`w-full max-w-sm rounded-2xl border border-blue-500/40 ${c.sheet} px-6 py-10 text-center`}>
          <h1 className="text-2xl font-semibold leading-snug">
            {profile.name} is reviewing your request...
          </h1>
          <div className="mt-8 flex justify-center">
            <Avatar src={profile.avatar} name={profile.name} size="lg" dark={dark} />
          </div>
          <p className={`mt-4 text-sm ${c.sub}`}>Active now</p>
          <button
            type="button"
            onClick={acceptChat}
            className="mt-6 rounded-full border border-blue-500/50 px-4 py-1.5 text-sm text-blue-400"
          >
            Request seen
          </button>
        </div>
      </div>
    );
  }

  const name = profile?.name || 'Chat';

  return (
    <div className={`h-[100dvh] ${c.root} flex flex-col`}>
      <header className={`flex items-center gap-2 border-b ${c.border} px-4 py-4 pt-[max(0.75rem,env(safe-area-inset-top))]`}>
        <Avatar src={profile?.avatar} name={profile?.name} dark={dark} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 font-semibold">
            <span className="truncate">{profile?.name}</span>
            <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-500 text-[10px]">✓</span>
          </div>
          <p className={`text-xs ${c.sub}`}>Active now</p>
        </div>
        <button
          type="button"
          aria-label="Toggle dark mode"
          onClick={toggleTheme}
          className={`flex h-10 w-10 items-center justify-center rounded-full ${c.icon}`}
        >
          {dark ? <SunIcon /> : <MoonIcon />}
        </button>
        <button
          type="button"
          aria-label="Voice call"
          onClick={() => setVoiceModalOpen(true)}
          className={`flex h-10 w-10 items-center justify-center rounded-full ${c.icon}`}
        >
          <PhoneIcon />
        </button>
        <div className="relative">
          <button
            type="button"
            aria-label="More options"
            onClick={() => setMenuOpen((open) => !open)}
            className={`flex h-10 w-10 items-center justify-center rounded-full ${c.icon}`}
          >
            <DotsIcon />
          </button>
          {menuOpen && (
            <>
              <button type="button" className="fixed inset-0 z-10 cursor-default" onClick={() => setMenuOpen(false)} aria-label="Close menu" />
              <div className={`absolute right-0 top-11 z-20 w-44 overflow-hidden rounded-xl border ${c.menu} shadow-xl`}>
                <button
                  type="button"
                  className={`block w-full px-4 py-2.5 text-left text-sm ${c.hover}`}
                  onClick={() => { setMenuOpen(false); setVoiceModalOpen(true); }}
                >
                  Voice call
                </button>
                <button
                  type="button"
                  className={`block w-full px-4 py-2.5 text-left text-sm text-red-400 ${c.hover}`}
                  onClick={() => { setMenuOpen(false); setReportDone(false); setReportOpen(true); }}
                >
                  Report
                </button>
              </div>
            </>
          )}
        </div>
      </header>

      <div ref={scroller} className="flex-1 overflow-y-auto px-3 py-4">
        <div className="mx-auto flex max-w-lg flex-col items-center pb-4 text-center">
          <div className="font-semibold">{profile?.name}</div>
          <p className={`mt-1 text-xs ${c.faint}`}>Messages are encrypted</p>
        </div>
        {acceptedAt && (
          <p className={`mb-3 text-center text-xs ${c.faint}`}>{formatWhen(acceptedAt)}</p>
        )}
        <div className="flex flex-col gap-2">
          {messages.map((msg) => {
            if (msg.contentType === 'system') {
              return <p key={msg.id} className={`py-1 text-center text-xs ${c.faint}`}>{msg.content}</p>;
            }
            const mine = msg.sender === 'user';
            return (
              <div key={msg.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                {!mine && <img src={profile?.avatar} alt="" className="mr-2 mt-1 h-8 w-8 rounded-full object-cover" />}
                <div className={`max-w-[80%] ${mine ? 'items-end' : 'items-start'} flex flex-col`}>
                  <div className="relative">
                    <div className={`rounded-2xl px-3 py-2 text-sm ${mine ? c.user : c.bot}`}>
                    {msg.contentType === 'image' && (
                      msg.local ? (
                        <img src={msg.content} alt="" className="max-h-64 rounded-lg object-cover" />
                      ) : (
                        <button type="button" onClick={() => setLightbox(msg.content)} className="text-left text-blue-400">
                          ▶ View photo
                        </button>
                      )
                    )}
                    {msg.contentType === 'audio' && (
                      msg.local ? (
                        <span className={dark ? 'text-zinc-300' : 'text-gray-600'}>Voice note…</span>
                      ) : (
                        <div>
                          <audio controls src={msg.content} className="h-8 max-w-full" />
                          {msg.transcript && <p className={`mt-1 text-xs ${dark ? 'text-zinc-300' : 'text-gray-600'}`}>{msg.transcript}</p>}
                        </div>
                      )
                    )}
                    {msg.contentType !== 'image' && msg.contentType !== 'audio' && msg.content && (
                      <p className="whitespace-pre-wrap break-words">{linkify(msg.content)}</p>
                    )}
                    </div>
                    {msg.reaction && (
                      <span className={`absolute -bottom-3 right-1 flex h-6 min-w-6 items-center justify-center rounded-full border px-1.5 text-sm shadow-sm ${dark ? 'bg-zinc-800 border-white/20' : 'bg-white border-black/10'}`}>
                        {msg.reaction}
                      </span>
                    )}
                  </div>
                  {renderStatus(msg)}
                </div>
              </div>
            );
          })}
          {typing && (
            <div className="flex justify-start">
              <img src={profile?.avatar} alt="" className="mr-2 mt-1 h-8 w-8 rounded-full object-cover" />
              <div className={`rounded-2xl px-4 py-3 ${c.bot}`}>
                <span className="flex items-center gap-1">
                  <span className={`h-2 w-2 rounded-full animate-bounce ${dark ? 'bg-zinc-400' : 'bg-gray-400'}`} />
                  <span className={`h-2 w-2 rounded-full animate-bounce ${dark ? 'bg-zinc-400' : 'bg-gray-400'}`} style={{ animationDelay: '150ms' }} />
                  <span className={`h-2 w-2 rounded-full animate-bounce ${dark ? 'bg-zinc-400' : 'bg-gray-400'}`} style={{ animationDelay: '300ms' }} />
                </span>
              </div>
            </div>
          )}
        </div>
        {error && <p className={`mt-3 text-center text-xs ${dark ? 'text-red-400' : 'text-red-500'}`}>{error}</p>}
      </div>

      <form
        onSubmit={sendText}
        className={`border-t ${c.border} px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]`}
      >
        {emojiOpen && (
          <div className={`mb-2 rounded-xl border ${c.sheet} p-3`}>
            <div className="mb-2 flex items-center justify-between px-1">
              <span className={`text-sm font-medium ${dark ? 'text-zinc-300' : 'text-gray-700'}`}>Emoji</span>
              <button type="button" onClick={() => setEmojiOpen(false)} className={`${dark ? 'text-zinc-500 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}>
                <CloseIcon size={18} />
              </button>
            </div>
            <div className="grid grid-cols-8 gap-1 overflow-y-auto" style={{ maxHeight: '40vh' }}>
              {EMOJIS.map((emoji) => (
                <button key={emoji} type="button" className={`flex items-center justify-center rounded-lg py-1 text-2xl ${dark ? 'hover:bg-white/10' : 'hover:bg-black/5'}`} onClick={() => setText((value) => value + emoji)}>
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className={`flex items-center gap-1 rounded-full ${c.composer} px-2 py-1`}>
          <button type="button" aria-label="Take photo" onClick={openCamera} className={`flex h-11 w-11 shrink-0 items-center justify-center ${c.icon}`}>
            <CameraIcon />
          </button>
          <input
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder="Message..."
            className={`min-w-0 flex-1 border-0 bg-transparent py-2 text-base outline-none appearance-none focus:outline-none focus:ring-0 ${c.placeholder}`}
          />
          <button type="button" aria-label="Record voice message" onClick={toggleRecord} className={`flex h-11 w-11 shrink-0 items-center justify-center ${recording ? 'text-red-400' : c.icon}`}>
            <MicIcon />
          </button>
          <button type="button" aria-label="Add emoji" onClick={() => setEmojiOpen((open) => !open)} className={`flex h-11 w-11 shrink-0 items-center justify-center ${emojiOpen ? 'text-yellow-400' : c.icon}`}>
            <SmileIcon />
          </button>
          <button type="button" aria-label="Attach file" onClick={() => fileRef.current?.click()} className={`flex h-11 w-11 shrink-0 items-center justify-center ${c.icon}`}>
            <ImageIcon />
          </button>
          {text.trim() && (
            <button type="submit" aria-label="Send" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
              <SendIcon size={20} />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(event) => { sendFile(event.target.files?.[0], 'image'); event.target.value = ''; }} />
        </div>
      </form>

      {cameraOpen && (
        <div className="fixed inset-0 z-20 flex flex-col bg-black">
          <video ref={videoRef} autoPlay playsInline muted className="min-h-0 flex-1 object-cover" />
          <div className="flex items-center justify-center gap-6 p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            <button type="button" onClick={stopCamera} className="text-sm text-zinc-300">Cancel</button>
            <button type="button" onClick={takePhoto} className="h-16 w-16 rounded-full border-4 border-white bg-white/80" aria-label="Shutter" />
          </div>
        </div>
      )}

      {lightbox && (
        <button type="button" className={`fixed inset-0 z-30 flex items-center justify-center ${c.lightbox} p-4`} onClick={() => setLightbox('')}>
          <img src={lightbox} alt="" className="max-h-full max-w-full rounded-lg object-contain" />
        </button>
      )}

      {voiceModalOpen && (
        <div className={`fixed inset-0 z-40 flex items-center justify-center ${c.backdrop} p-4`} onClick={() => setVoiceModalOpen(false)}>
          <div className={`w-full max-w-xs rounded-2xl ${c.modal} p-6 text-center`} onClick={(event) => event.stopPropagation()}>
            <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${dark ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-500'}`}>
              <PhoneIcon size={26} />
            </div>
            <h2 className={`mt-4 text-lg font-semibold ${dark ? 'text-white' : 'text-black'}`}>Voice calls unavailable</h2>
            <p className={`mt-2 text-sm ${c.modalBody}`}>
              {name} has disabled voice calls
            </p>
            <button
              type="button"
              onClick={() => setVoiceModalOpen(false)}
              className="mt-5 w-full rounded-full bg-blue-600 py-2 text-sm font-medium text-white"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {reportOpen && (
        <div className={`fixed inset-0 z-40 flex flex-col ${c.root}`}>
          <header className={`flex items-center gap-2 border-b ${c.border} px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]`}>
            <button
              type="button"
              aria-label="Back"
              onClick={() => { setReportOpen(false); setReportDone(false); }}
              className={`flex h-10 w-10 items-center justify-center ${c.icon}`}
            >
              <BackIcon />
            </button>
            <h2 className="flex-1 text-center text-base font-semibold">Report</h2>
            <span className="w-10" />
          </header>
          <div className="flex-1 overflow-y-auto px-4 py-5">
            {reportDone ? (
              <div className="flex h-full flex-col items-center justify-center text-center">
                <div className={`flex h-14 w-14 items-center justify-center rounded-full ${dark ? 'bg-zinc-800 text-green-400' : 'bg-green-100 text-green-600'}`}>
                  <CheckIcon size={28} />
                </div>
                <p className={`mt-4 max-w-xs text-base font-medium ${dark ? 'text-white' : 'text-black'}`}>
                  Report submitted, thank you for keeping our platform safe.
                </p>
              </div>
            ) : (
              <>
                <p className={`text-sm leading-relaxed ${dark ? 'text-zinc-300' : 'text-gray-700'}`}>
                  You can report this chat if you think it goes against our Community Standards. We won't notify the account that you submitted this report.
                </p>
                <div className={`mt-4 flex flex-col overflow-hidden rounded-xl border ${c.border}`}>
                  {REPORT_REASONS.map((reason, idx) => (
                    <button
                      key={reason}
                      type="button"
                      onClick={submitReport}
                      className={`w-full px-4 py-3.5 text-left text-sm ${c.hover} ${idx > 0 ? `border-t ${c.border}` : ''}`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
